/**
 * ChakraArchitectureInitializer - Setup for separated chakra rendering system
 *
 * Initializes the event-driven architecture with specialized renderers:
 * - TabChartCoordinator: DOM coordination and event routing
 * - GrahaChakraRenderer: Planet rendering (Graha Chakra)
 * - MuhurtaChakraDivisionsRenderer: Muhurta divisions rendering
 */

import { devLog } from '../../../../../utils/devLog';
import { EventStream, globalEventStream } from '../core/EventStream';
import { TabChartCoordinator } from './TabChartCoordinator';
import { GrahaChakraRenderer } from './GrahaChakraRenderer';
import { ReadyChartData } from '../../../../../types/ReadyChartData';

export interface ChakraArchitectureConfig {
    eventStream?: EventStream;
    enableGlobalGrahaRenderer?: boolean;
    debugMode?: boolean;
    defaultChakraSettings?: {
        showGrahaChakra?: boolean;
        showMuhurtaChakra?: boolean;
    };
}

/**
 * Separated Chakra Architecture Manager
 */
export class ChakraArchitectureInitializer {
    private static instance: ChakraArchitectureInitializer | null = null;
    private eventStream: EventStream;
    private eventSubscriptions: Array<{ unsubscribe: () => void }> = [];
    private globalGrahaRenderer: GrahaChakraRenderer | null = null;
    private coordinators: Map<string, TabChartCoordinator> = new Map();
    private isInitialized: boolean = false;

    private constructor(config: ChakraArchitectureConfig = {}) {
        this.eventStream = config.eventStream || globalEventStream;
        // Debug: Initialized (production: logs disabled)
    }

    /**
     * Get singleton instance
     */
    public static getInstance(config?: ChakraArchitectureConfig): ChakraArchitectureInitializer {
        if (!ChakraArchitectureInitializer.instance) {
            ChakraArchitectureInitializer.instance = new ChakraArchitectureInitializer(config);
        }
        return ChakraArchitectureInitializer.instance;
    }

    /**
     * Initialize the separated chakra architecture
     */
    public initialize(config: ChakraArchitectureConfig = {}): void {
        if (this.isInitialized) {
            devLog('🔄 ChakraArchitectureInitializer: Already initialized, skipping');
            return;
        }

        devLog('🚀 ChakraArchitectureInitializer: Initializing separated chakra architecture...');

        // Initialize global GrahaChakraRenderer if enabled (default: true)
        if (config.enableGlobalGrahaRenderer !== false) {
            this.initializeGlobalGrahaRenderer();
        }

        // Setup architecture-level event listeners
        this.setupArchitectureEvents();

        this.isInitialized = true;
        devLog('✅ ChakraArchitectureInitializer: Separated chakra architecture ready');
    }

    /**
     * Create a new chart coordinator
     */
    public createChartCoordinator(chartId: string, debugMode: boolean = false): TabChartCoordinator {
        if (this.coordinators.has(chartId)) {
            devLog(`♻️ ChakraArchitectureInitializer: Reusing existing coordinator for ${chartId}`);
            return this.coordinators.get(chartId)!;
        }

        devLog(`🎯 ChakraArchitectureInitializer: Creating new coordinator for ${chartId}`);

        const coordinator = new TabChartCoordinator(this.eventStream, debugMode, chartId);
        this.coordinators.set(chartId, coordinator);

        return coordinator;
    }

    /**
     * Render chart using separated architecture
     */
    public async renderChart(
        chartId: string,
        chartData: ReadyChartData,
        options: {
            displayMode?: 'symbols' | 'short';
            showGrahaChakra?: boolean;
            showMuhurtaChakra?: boolean;
            enableAnimations?: boolean;
            debugMode?: boolean;
        } = {}
    ): Promise<{
        success: boolean;
        coordinatorResult: any;
    }> {
        devLog(`🎨 ChakraArchitectureInitializer: Rendering chart ${chartId} with separated architecture`);

        // Ensure initialization
        if (!this.isInitialized) {
            this.initialize();
        }

        // Get or create coordinator
        const coordinator = this.createChartCoordinator(chartId, options.debugMode || false);

        // Coordinate rendering through events
        const coordinatorResult = await coordinator.coordinateChart(chartData, {
            displayMode: options.displayMode || 'short',
            showGrahaChakra: options.showGrahaChakra !== false,
            showMuhurtaChakra: options.showMuhurtaChakra !== false,
            enableAnimations: options.enableAnimations || false,
            debugMode: options.debugMode || false
        });

        devLog(`✅ ChakraArchitectureInitializer: Chart ${chartId} coordination completed:`, coordinatorResult);

        return {
            success: coordinatorResult.success,
            coordinatorResult
        };
    }

    /**
     * Initialize global GrahaChakraRenderer
     */
    private initializeGlobalGrahaRenderer(): void {
        if (this.globalGrahaRenderer) {
            devLog('🌟 ChakraArchitectureInitializer: Global GrahaChakraRenderer already initialized');
            return;
        }

        devLog('🌟 ChakraArchitectureInitializer: Initializing global GrahaChakraRenderer...');

        this.globalGrahaRenderer = new GrahaChakraRenderer(this.eventStream);

        // Register globally for access
        (window as any).globalGrahaRenderer = this.globalGrahaRenderer;

        devLog('✅ ChakraArchitectureInitializer: Global GrahaChakraRenderer ready');
    }

    /**
     * Setup architecture-level event listeners
     */
    private setupArchitectureEvents(): void {
        devLog('🔗 ChakraArchitectureInitializer: Setting up architecture-level events');

        // Listen for coordinator creation requests
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chart.coordinator.create', (event: any) => {
                if (event.chartId) {
                    this.createChartCoordinator(event.chartId, event.debugMode || false);
                }
            })
        );

        // Listen for architecture status requests
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chakra.architecture.status', (event: any) => {
                const status = this.getArchitectureStatus();
                this.eventStream.emit({
                    id: `chakra-architecture-status-${Date.now()}`,
                    type: 'chakra.architecture.status.response',
                    source: 'chakra-architecture-initializer',
                    timestamp: Date.now(),
                    metadata: status
                });
            })
        );

        // Listen for cleanup requests
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chakra.architecture.cleanup', (event: any) => {
                this.cleanup();
            })
        );
    }

    /**
     * Toggle Graha Chakra for all charts
     */
    public toggleGlobalGrahaChakra(visible: boolean): void {
        devLog(`👁️ ChakraArchitectureInitializer: Toggling global Graha Chakra to ${visible}`);

        this.coordinators.forEach((coordinator, chartId) => {
            coordinator.toggleGrahaChakra(visible);
        });
    }

    /**
     * Toggle Muhurta Chakra for all charts
     */
    public toggleGlobalMuhurtaChakra(visible: boolean): void {
        devLog(`📏 ChakraArchitectureInitializer: Toggling global Muhurta Chakra to ${visible}`);

        this.coordinators.forEach((coordinator, chartId) => {
            coordinator.toggleMuhurtaChakra(visible);
        });
    }

    /**
     * Get architecture status
     */
    public getArchitectureStatus() {
        return {
            isInitialized: this.isInitialized,
            globalGrahaRenderer: !!this.globalGrahaRenderer,
            coordinatorCount: this.coordinators.size,
            coordinators: Array.from(this.coordinators.keys()),
            grahaRendererStats: this.globalGrahaRenderer?.getAllStatuses() || {},
            coordinatorStats: Object.fromEntries(
                Array.from(this.coordinators.entries()).map(([chartId, coordinator]) => [
                    chartId,
                    coordinator.getStats()
                ])
            )
        };
    }

    /**
     * Get coordinator for chart ID
     */
    public getCoordinator(chartId: string): TabChartCoordinator | null {
        return this.coordinators.get(chartId) || null;
    }

    /**
     * Get global GrahaChakraRenderer
     */
    public getGlobalGrahaRenderer(): GrahaChakraRenderer | null {
        return this.globalGrahaRenderer;
    }

    /**
     * Cleanup resources
     */
    public cleanup(): void {
        devLog('🧹 ChakraArchitectureInitializer: Cleaning up resources...');

        // Unsubscribe from all event subscriptions
        this.eventSubscriptions.forEach(sub => sub.unsubscribe());
        this.eventSubscriptions = [];

        this.coordinators.forEach((coordinator, chartId) => {
            coordinator.cleanup();
        });
        this.coordinators.clear();

        if (this.globalGrahaRenderer) {
            this.globalGrahaRenderer.destroy();
            // Clear global reference
            delete (window as any).globalGrahaRenderer;
            this.globalGrahaRenderer = null;
        }

        this.isInitialized = false;
        devLog('✅ ChakraArchitectureInitializer: Cleanup completed');
    }
}

/**
 * Global instance for easy access
 */
export const globalChakraArchitecture = ChakraArchitectureInitializer.getInstance();

/**
 * Convenience function to initialize separated chakra architecture
 */
export function initializeChakraArchitecture(config: ChakraArchitectureConfig = {}): ChakraArchitectureInitializer {
    const architecture = ChakraArchitectureInitializer.getInstance(config);
    architecture.initialize(config);
    return architecture;
}

/**
 * Convenience function to render chart with separated architecture
 */
export async function renderChartWithSeparatedArchitecture(
    chartId: string,
    chartData: ReadyChartData,
    options: Parameters<ChakraArchitectureInitializer['renderChart']>[2] = {}
): Promise<ReturnType<ChakraArchitectureInitializer['renderChart']>> {
    return globalChakraArchitecture.renderChart(chartId, chartData, options);
}